/**
 * Centralized URL builders for deep-linking from inventory-planning into
 * inventory_staging. All callers use these helpers instead of inlining
 * `process.env["REACT_APP_INVENTORY_STAGING_URL"]` or string-concatenating
 * URLs, so the base URL and route shape live in one place.
 *
 * Every link produced here is intended to be opened in a new browser tab
 * via the <ExternalLink> component (or, for non-link triggers, via
 * `window.open(url, "_blank", "noopener,noreferrer")`).
 */

const STAGING = process.env["REACT_APP_INVENTORY_STAGING_URL"] ?? "http://localhost:3001";

type QsValue = string | number | null | undefined;

/**
 * Build a `?a=1&b=2` query string from an object.
 *
 * Why: prevents `?productId=undefined` from sneaking into URLs when a caller
 * passes an optional ID through (e.g. `stagingLinks.createMovement({ productId })`
 * where `productId` is `number | undefined`).
 */
const qs = (params: Record<string, QsValue>): string => {
  const entries = Object.entries(params).filter(
    ([, v]) => v !== null && v !== undefined && v !== ""
  );
  if (entries.length === 0) return "";
  const search = new URLSearchParams(entries.map(([k, v]) => [k, String(v)])).toString();
  return `?${search}`;
};

export const stagingLinks = {
  /** Base URL — for nav items and `<a>` to the staging home. */
  base: (): string => STAGING,

  // ---- Detail pages (numeric IDs only — guards against `${undefined}`) ----

  product: (id: number): string => `${STAGING}/products/${id}`,
  movement: (id: number): string => `${STAGING}/movements/${id}`,
  rebalance: (id: number): string => `${STAGING}/rebalances/${id}`,
  purchaseOrder: (id: number): string => `${STAGING}/purchase-orders/${id}`,
  adjustment: (id: number): string => `${STAGING}/inventory-adjustments/${id}`,
  shipstationOrder: (id: number): string => `${STAGING}/shipstation/orders/${id}`,
  amazonOrder: (id: number): string => `${STAGING}/amazon/orders/${id}`,
  walmartOrder: (customerOrderId: string): string =>
    `${STAGING}/walmart/orders/${encodeURIComponent(customerOrderId)}`,

  /**
   * Snapshots are routed by ISO date (`/count-snapshots/YYYY-MM-DD`) because
   * the planning API surfaces `snapshot_datetime` (nullable) but not
   * `snapshot_id`. Returns `null` when no date is available so <ExternalLink>
   * can skip rendering a broken link.
   */
  countSnapshot: (isoDatetime: string | null | undefined): string | null => {
    if (isoDatetime === null || isoDatetime === undefined || isoDatetime === "") {
      return null;
    }
    return `${STAGING}/count-snapshots/${isoDatetime.slice(0, 10)}`;
  },

  /** Inventory-ledger drilldown with optional pre-filter params. */
  ledger: (params: { productId?: number; locationId?: number; snapshotId?: number }): string =>
    `${STAGING}/inventory-ledger${qs({
      product_id: params.productId,
      location_id: params.locationId,
      snapshot_id: params.snapshotId,
    })}`,

  // ---- Create-modal deep-links (migrated from inline window.open calls) ----

  createPO: (productId: number): string =>
    `${STAGING}/purchase-orders${qs({ modal: "create", productId })}`,

  createMovement: (opts: { productId?: number; purchaseOrderId?: number }): string =>
    `${STAGING}/movements${qs({
      modal: "create",
      productId: opts.productId,
      purchaseOrderId: opts.purchaseOrderId,
    })}`,

  createRebalance: (opts: { productId: number; toLocation?: string }): string =>
    `${STAGING}/rebalances${qs({
      modal: "create",
      productId: opts.productId,
      toLocation: opts.toLocation,
    })}`,
};

// Intentionally **no** `location(id)` builder: planning uses slug location IDs
// (`"kc"`, `"amazon_us"`) but staging /locations/:id fetches by numeric DB id,
// so a slug-based link would 404. Adding the builder requires exposing numeric
// location_id from the planning API.
